// @ts-strict-ignore
import * as React from 'react';
import { ReleaseCalendarLifecycleEventData, getStepForLifecycleEvent } from '@/helpers';
import { RootState } from '../../reducers/reducers';
import {
    AutomationPromoteStablePhasedRelease,
    AutomationRun,
    AutomationType,
    LifecycleEventType,
    ReleaseCalendarEventType,
    ReleaseCalendarViewType,
    ReleaseStatus,
} from '@/types';
import { findLifecycleAutomationRun, targetDateLabelIconDateString } from '../automations/automationHelperComponents';
import dayjs from 'dayjs';
import { antGrey, runwayColors, rwColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import ReleaseCalendarEventPopover from './ReleaseCalendarEventPopover';
import { displayLifecycleEventFullTime } from '@/helpers/displayHelpers';
import { useRef, useEffect, useState } from 'react';
import { selectAppSettingAutomations, selectDestinationInfoByDestinationStepConfigId } from '@/selectors/selectors';
import { useSelector } from 'react-redux';
import { getReleaseCalendarDotState, ReleaseCalendarDotState } from './ReleaseCalendarDot';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { Destination } from '@/types/destinations';

interface ReleaseCalendarLifecycleEventDataProps {
    event: ReleaseCalendarLifecycleEventData;
    lifecycleAutomations?: AutomationRun[];
    view: ReleaseCalendarViewType;
    orgId: string;
    appId: string;
    releaseId?: string;
}

export const ReleaseCalendarLifecycleEvent = (props: ReleaseCalendarLifecycleEventDataProps) => {
    let automationRun = null;
    const event = props.event;

    if (props.lifecycleAutomations) {
        automationRun = findLifecycleAutomationRun(
            getStepForLifecycleEvent(event.eventType),
            props.lifecycleAutomations,
        );
    }
    const eventDate = dayjs(event.start);
    const automationEnabled =
        automationRun && automationRun.enabled && eventDate.isSame(automationRun.scheduledAt, 'day');
    const automation = automationEnabled ? automationRun : null;
    let automationIcon: React.ReactNode = null;
    if (automationEnabled && event.start) {
        const { icon } = targetDateLabelIconDateString(automation, eventDate);
        automationIcon = icon;
    }

    let textStyle = {};
    if (
        event.status === ReleaseStatus.Skipped &&
        (event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ||
            event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate)
    ) {
        textStyle = { textDecoration: 'line-through' };
    } else if (event.eventType === LifecycleEventType.LifecycleEventTypeBlackoutPeriod) {
        let textStyleColor = antGrey.grey7;
        const blackoutStart = dayjs(event.start);
        const blackoutEnd = dayjs(event.end);
        if (!blackoutStart.isSame(blackoutEnd, 'day')) {
            textStyleColor = runwayColors.white;
        }
        textStyle = { color: textStyleColor, fontStyle: 'italic' as const };
    }

    const destinationInfoById = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, props.appId),
    );
    const destinationsByEventId = React.useMemo(() => {
        const byId: Record<string, Destination> = {};
        Object.values(destinationInfoById).forEach((destination) => {
            byId[destination.destinationId ?? `dest-${destination.stepConfigId}`] = destination;
            byId[destination.stepConfigId] = destination;
        });
        return byId;
    }, [destinationInfoById]);
    const eventDestinations = (event.destinationIds ?? [])
        .map((destinationId) => destinationsByEventId[destinationId])
        .filter(Boolean);

    const dotState = getReleaseCalendarDotState(event, dayjs());
    const isOverdue = dotState === ReleaseCalendarDotState.Overdue;
    const isPredictedRollout = dotState === ReleaseCalendarDotState.RolloutIncrement;
    const isDone = dotState === ReleaseCalendarDotState.Actual;

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId));
    const autoPromoteStablePhasedReleasesAutomation = automations.find(
        (automation) => automation.type === AutomationType.AutoPromoteStablePhasedReleases,
    ) as AutomationPromoteStablePhasedRelease;

    const isAutoPromoteStablePhasedReleasesEnabled = autoPromoteStablePhasedReleasesAutomation?.enabled;
    const hasPhasedReleaseDayThreshold =
        !!autoPromoteStablePhasedReleasesAutomation?.data?.phasedReleaseDayThreshold ||
        !!autoPromoteStablePhasedReleasesAutomation?.data?.stagedRolloutPercentageThreshold;
    const lifecycleEventsForDetails = event.groupedEvents ?? [event];

    const titleRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const titlePrefixRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [titleWidth, setTitleWidth] = useState(0);

    useEffect(() => {
        const containerElement = containerRef.current;
        const titleElement = titleRef.current;
        const titlePrefixElement = titlePrefixRef.current;
        if (!containerElement || !titleElement || !titlePrefixElement) return () => {};

        const observer = new ResizeObserver(() => {
            const hasHorizontalOverflow =
                titlePrefixElement.scrollWidth + titleElement.scrollWidth >= containerElement.clientWidth * 0.8;
            setIsOverflowing(hasHorizontalOverflow);

            if (hasHorizontalOverflow) {
                setTitleWidth((containerElement.clientWidth - titlePrefixElement.scrollWidth) * 0.8);
            }
        });

        observer.observe(containerElement);

        return () => observer.disconnect();
    }, []);

    const popoverProps = {
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId ?? props.appId + event.id,
        backgroundColor: event.backgroundColor,
        displayVersion: event.displayVersion,
        lifecycleAutomations: props.lifecycleAutomations ?? null,
        eventDateType: event.eventDateType,
        eventType: event.eventType,
        isEventComplete: event.isEventComplete,
        eventDate: event.start,
        isTargetDateScheduledFromCadence: event.targetDateSetByCadence,
        targetDateManuallyUpdatedAt: event.targetDateManuallyUpdatedAt,
        targetDateManuallyUpdatedByUserId: event.targetDateManuallyUpdatedByUserId,
        incrementDayNumber: event.incrementDayNumber,
        stagedRolloutPercentage: event.stagedRolloutPercentage,
        isSkippedRelease: event.status === ReleaseStatus.Skipped,
        isExternalEvent: false,
        eventEndDate: event.end,
        shouldAutoAccelerateBasedOnPhasedReleaseDay:
            isAutoPromoteStablePhasedReleasesEnabled && hasPhasedReleaseDayThreshold,
        lifecycleEventsForDetails,
    };

    const lifecyclePill = (
        <span
            className={[
                'release-calendar-v2-lifecycle-pill',
                isPredictedRollout && 'release-calendar-v2-lifecycle-pill-dashed',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {eventDestinations.length > 1 && <DestinationCounter destinations={eventDestinations} size={16} />}
            <Text className="release-calendar-v2-lifecycle-title" style={textStyle}>
                {event.title}
            </Text>
        </span>
    );

    if (props.view === ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth) {
        return (
            <div
                className={[
                    'release-calendar-v2-lifecycle-event',
                    isPredictedRollout && 'release-calendar-v2-lifecycle-event-predicted',
                    isDone && 'release-calendar-v2-lifecycle-event-done',
                ]
                    .filter(Boolean)
                    .join(' ')}
                style={{ '--rw-lifecycle-color': event.backgroundColor } as React.CSSProperties}
                data-lifecycle-pill-id={event.id}
            >
                <Text
                    className="release-calendar-v2-lifecycle-time"
                    style={{
                        ...textStyle,
                        ...(isOverdue && { color: rwColors.statusRed, textDecoration: 'line-through' }),
                    }}
                >
                    {displayLifecycleEventFullTime(event.start)}
                </Text>
                <ReleaseCalendarEventPopover {...popoverProps} eventContent={lifecyclePill} />
                {automationIcon}
            </div>
        );
    }

    const eventContent =
        props.view === ReleaseCalendarViewType.ReleaseCalendarViewTypeWeek ?
            <div ref={containerRef} style={{ width: '100%' }}>
                <Space size="small">
                    <div ref={titlePrefixRef}>
                        <Text style={{ ...textStyle, color: runwayColors.white, whiteSpace: 'nowrap' }}>
                            {displayLifecycleEventFullTime(event.start)}
                        </Text>
                    </div>
                    <div ref={titleRef}>
                        <Text
                            style={{
                                ...textStyle,
                                maxWidth: '100%',
                                color: runwayColors.white,
                                whiteSpace: 'nowrap',
                                ...(isOverflowing && {
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    width: titleWidth,
                                }),
                            }}
                        >
                            {event.title}
                        </Text>
                    </div>
                </Space>
            </div>
        : props.view === ReleaseCalendarViewType.ReleaseCalendarViewTypeDay ?
            <Space size="small">
                <Text style={{ ...textStyle, color: runwayColors.white }}>
                    {displayLifecycleEventFullTime(event.start)}
                </Text>
                <Text style={{ ...textStyle, color: runwayColors.white }}>{event.title}</Text>
            </Space>
        :   <Text style={textStyle}>{event.title}</Text>;

    return <ReleaseCalendarEventPopover {...popoverProps} eventContent={eventContent} />;
};
